import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { proxyCall } from "./proxy";
import { slugFilter } from "./slug";
import { parseStored } from "./persist";
import { orgName } from "./engagement";
import { T } from "./theme";
import ClientView from "./client/ClientView";
import normalizeConfig from "./finance/normalizeConfig";
import validateConfig from "./finance/validateConfig";
import { getCurrentMonth } from "./finance/calc";

// The client's page: load the engagement, show the read-only view. Every
// edit Thread makes happens in the admin workspace (/admin/:slug).
export default function EngagementPage() {
  const { slug } = useParams();
  const [engagement, setEngagement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  // Remembered per engagement in this browser. A convenience, not security (docs/SECURITY.md).
  const authKey = `thread-hub:auth:${slug}`;
  const [authed, setAuthed] = useState(() => { try { return localStorage.getItem(authKey) === "1"; } catch (e) { return false; } });
  const [pw, setPw] = useState("");
  const [pwError, setPwError] = useState(false);
  const [steps, setSteps] = useState({});
  const [cfg, setCfg] = useState(() => normalizeConfig(null));
  const [configProblems, setConfigProblems] = useState([]);
  const [data, setData] = useState({ months: {}, upcoming: [], extras: [], sessions: [], history: [] });

  useEffect(() => { loadEngagement(); }, [slug]);

  const loadEngagement = async () => {
    setLoading(true);
    const filter = slugFilter(slug);
    if (!filter) { setNotFound(true); setLoading(false); return; }
    try {
      const res = await proxyCall({ action: "list", filterByFormula: filter, maxRecords: 1 });
      const c = res.records && res.records[0];
      if (!c) { setNotFound(true); setLoading(false); return; }
      setEngagement(c);
      const stored = parseStored(c.fields);
      if (stored.steps) setSteps(stored.steps);
      if (stored.config) { setCfg(normalizeConfig(stored.config)); setConfigProblems(validateConfig(stored.config)); }
      const fd = stored.data || {};
      setData({ months: fd.months || {}, upcoming: fd.upcoming || [], extras: Array.isArray(fd.extras) ? fd.extras : [], sessions: fd.sessionBlock?.sessions || [], history: fd.sessionBlock?.history || [] });
    } catch (e) { console.error(e); setNotFound(true); }
    setLoading(false);
  };

  const signOut = () => { try { localStorage.removeItem(authKey); } catch (e) {} setAuthed(false); setPw(""); };
  const login = () => {
    if (engagement && pw === engagement.fields.Password) { setAuthed(true); setPwError(false); try { localStorage.setItem(authKey, "1"); } catch (e) {} }
    else setPwError(true);
  };

  if (loading) return (<div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: T.bgSurface, fontFamily: T.fontSans }}><p style={{ color: T.fgMuted, fontSize: 14 }}>Loading...</p></div>);
  if (notFound) return (<div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 12, background: T.bgSurface, fontFamily: T.fontSans }}><h2 style={{ fontSize: 20, fontWeight: 700, color: T.root }}>Page not found</h2><p style={{ color: T.fgMuted, fontSize: 14 }}>Check the URL and try again.</p></div>);

  const f = engagement.fields;
  const company = orgName(f);

  if (!authed) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: T.bgInverse, fontFamily: T.fontSans }}>
      <div className="login-card" style={{ background: T.canvas, borderRadius: T.radiusLg, padding: 40, width: 380, textAlign: "center", boxShadow: T.shadowLg }}>
        <div style={{ color: T.canopy, fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 8 }}>Thread Hub</div>
        {f["Logo URL"] && (<div style={{ width: 200, height: 80, margin: "0 auto 16px", display: "flex", alignItems: "center", justifyContent: "center" }}><img src={f["Logo URL"]} alt={company} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} /></div>)}
        <h2 style={{ fontSize: 22, fontWeight: 700, color: T.root, marginBottom: 4 }}>{company}</h2>
        <p style={{ color: T.fgMuted, fontSize: 13, marginBottom: 24 }}>Enter your password to continue</p>
        <input type="password" value={pw} onChange={e => { setPw(e.target.value); setPwError(false); }} onKeyDown={e => e.key === "Enter" && login()} placeholder="Password" autoComplete="current-password" style={{ width: "100%", padding: "12px 16px", fontSize: 14, border: `1px solid ${pwError ? T.statusOff : T.borderSubtle}`, borderRadius: T.radiusMd, outline: "none", marginBottom: 12, fontFamily: T.fontSans, background: T.bgSurface }} />
        {pwError && <p style={{ color: T.statusOff, fontSize: 12, marginBottom: 12 }}>Incorrect password</p>}
        <button onClick={login} style={{ width: "100%", padding: "12px 0", fontSize: 14, fontWeight: 600, color: T.fgOnDark, background: T.root, border: "none", borderRadius: T.radiusMd, cursor: "pointer", fontFamily: T.fontSans }}>Continue</button>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: T.bgSurface, fontFamily: T.fontSans }}>
      <div className="hub-header" style={{ background: T.bgInverse, padding: "12px 40px" }}>
        <div className="hub-header-inner" style={{ maxWidth: 840, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0 }}>
            {f["Logo URL"] && (<div style={{ width: 96, height: 32, display: "flex", alignItems: "center", flex: "none" }}><img src={f["Logo URL"]} alt="" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} /></div>)}
            <div style={{ minWidth: 0 }}>
              <div style={{ color: T.canopy, fontSize: 10, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase" }}>Client portal</div>
              <h1 style={{ color: T.fgOnDark, fontSize: 16, fontWeight: 700, margin: 0, lineHeight: 1.3 }}>{company}</h1>
            </div>
          </div>
          <button onClick={signOut} style={{ fontSize: 12, color: T.canopy, background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: T.fontSans }}>Sign out</button>
        </div>
      </div>
      <div>
        <ClientView
          fields={f} cfg={cfg} configProblems={configProblems} steps={steps}
          finance={data.months} upcoming={data.upcoming} extras={data.extras} blockSessions={data.sessions} blockHistory={data.history}
          currentMonth={getCurrentMonth()}
        />
      </div>
    </div>
  );
}
